import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

const CheckIcon = () => (
    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

export default function UseCasesSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);

    const variant = config.variant || 'tabs';
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;

    const cases = (content.cases || []).map(c => ({ ...c, ...resolveItemMedia(c, mediaMode, mediaFolders) }));

    const [activeIdx, setActiveIdx] = useState(0);
    const [prevIdx, setPrevIdx] = useState(null);
    const [transitioning, setTransitioning] = useState(false);

    const handleSelect = (i) => {
        if (i === activeIdx || transitioning) return;
        setPrevIdx(activeIdx);
        setTransitioning(true);
        setTimeout(() => {
            setActiveIdx(i);
            setTransitioning(false);
            setPrevIdx(null);
        }, 220);
    };

    if (cases.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle };
    const active = cases[activeIdx] || cases[0];
    const hasMedia = !!(active.resolvedImage || active.carouselImages || active.video);
    const accentColor = config.accent_color || s.accent;

    /* ── Selector rendering helpers ── */
    const SelectorTabs = () => (
        <div className="relative flex items-end gap-0 border-b" style={{ borderColor: hexToRgba(accentColor, 0.15) }}>
            {cases.map((c, i) => {
                const isActive = i === activeIdx;
                return (
                    <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        className="relative px-5 py-3 text-sm font-semibold transition-colors duration-200 whitespace-nowrap focus:outline-none"
                        style={{ color: isActive ? accentColor : s.textSub }}
                    >
                        {c.icon && <span className="mr-2">{c.icon}</span>}
                        {c.role || c.title}
                        {isActive && (
                            <span
                                className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full"
                                style={{ backgroundColor: accentColor }}
                            />
                        )}
                    </button>
                );
            })}
        </div>
    );

    const SelectorPills = () => (
        <div className="flex flex-wrap gap-2 justify-center">
            {cases.map((c, i) => {
                const isActive = i === activeIdx;
                return (
                    <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 focus:outline-none hover:scale-105 active:scale-95"
                        style={{
                            backgroundColor: isActive ? accentColor : hexToRgba(accentColor, 0.08),
                            color: isActive ? '#ffffff' : s.text,
                            boxShadow: isActive ? `0 4px 16px ${hexToRgba(accentColor, 0.3)}` : 'none',
                        }}
                    >
                        {c.icon && <span className="mr-2">{c.icon}</span>}
                        {c.role || c.title}
                    </button>
                );
            })}
        </div>
    );

    /* ── Content panel (used by tabs + pills) ── */
    const ContentPanel = () => (
        <div
            className={`mt-8 grid grid-cols-1 ${hasMedia ? 'lg:grid-cols-2' : ''} gap-10 items-center`}
            style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(10px)' : 'translateY(0)',
                transition: 'opacity 0.22s ease, transform 0.22s ease',
            }}
        >
            {/* Text side */}
            <div className="flex flex-col gap-5">
                <div>
                    {active.icon && (
                        <div
                            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4 text-2xl"
                            style={{ backgroundColor: hexToRgba(accentColor, 0.1), color: accentColor }}
                        >
                            {active.icon}
                        </div>
                    )}
                    {active.title && (
                        <h3 className="text-2xl md:text-3xl font-bold mb-3 leading-tight" style={{ color: s.text }}>
                            {active.title}
                        </h3>
                    )}
                    {active.description && (
                        <p className="text-base leading-relaxed" style={{ color: s.textSub }}>
                            {active.description}
                        </p>
                    )}
                </div>

                {/* Features list */}
                {Array.isArray(active.features) && active.features.length > 0 && (
                    <ul className="flex flex-col gap-2.5">
                        {active.features.map((feat, fi) => (
                            <li key={fi} className="flex items-start gap-2.5 text-sm" style={{ color: s.text }}>
                                <span style={{ color: accentColor }}><CheckIcon /></span>
                                <span>{feat}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {/* CTA */}
                {active.cta_text && active.cta_link && (
                    <a
                        {...resolveLinkProps(active.cta_link)}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:scale-105 active:scale-95 self-start mt-2"
                        style={{ backgroundColor: accentColor, color: '#ffffff' }}
                    >
                        {active.cta_text}
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </a>
                )}
            </div>

            {/* Media side */}
            {hasMedia && (
                <div className="w-full">
                    <MediaRenderer
                        item={active}
                        fitClass={fitClass}
                        interval={carouselInterval}
                        accent={accentColor}
                        aspect="aspect-[4/3]"
                        rounded="rounded-2xl"
                        className="w-full shadow-2xl"
                    />
                </div>
            )}
        </div>
    );

    /* ── CARDS variant ── */
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                        {cases.map((c, i) => {
                            const isActive = i === activeIdx;
                            return (
                                <button
                                    key={i}
                                    onClick={() => handleSelect(i)}
                                    className={`${card} text-left p-6 transition-all duration-300 focus:outline-none ${
                                        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                    } ${isActive ? 'ring-2' : 'hover:-translate-y-1'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 80}ms` : '0ms',
                                        ringColor: accentColor,
                                        ...(isActive ? { boxShadow: `0 0 0 2px ${accentColor}, 0 8px 30px ${hexToRgba(accentColor, 0.2)}` } : {}),
                                    }}
                                >
                                    {c.icon && (
                                        <div
                                            className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3 text-xl transition-all"
                                            style={{
                                                backgroundColor: isActive ? accentColor : hexToRgba(accentColor, 0.1),
                                                color: isActive ? '#ffffff' : accentColor,
                                            }}
                                        >
                                            {c.icon}
                                        </div>
                                    )}
                                    <h3 className="text-base font-bold mb-1" style={{ color: s.text }}>
                                        {c.role || c.title}
                                    </h3>
                                    {c.description && (
                                        <p className="text-sm line-clamp-2" style={{ color: s.textSub }}>{c.description}</p>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Expanded content for selected card */}
                    <div
                        className={`${card} overflow-hidden transition-all duration-300`}
                        style={{
                            opacity: transitioning ? 0 : 1,
                            transform: transitioning ? 'translateY(8px)' : 'translateY(0)',
                        }}
                    >
                        <div className={`p-6 md:p-8 grid grid-cols-1 ${hasMedia ? 'lg:grid-cols-2' : ''} gap-8 items-center`}>
                            <div className="flex flex-col gap-4">
                                {active.title && (
                                    <h3 className="text-2xl font-bold" style={{ color: s.text }}>{active.title}</h3>
                                )}
                                {active.description && (
                                    <p className="text-base leading-relaxed" style={{ color: s.textSub }}>{active.description}</p>
                                )}
                                {Array.isArray(active.features) && active.features.length > 0 && (
                                    <ul className="flex flex-col gap-2">
                                        {active.features.map((feat, fi) => (
                                            <li key={fi} className="flex items-start gap-2.5 text-sm" style={{ color: s.text }}>
                                                <span style={{ color: accentColor }}><CheckIcon /></span>
                                                <span>{feat}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {active.cta_text && active.cta_link && (
                                    <a
                                        {...resolveLinkProps(active.cta_link)}
                                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:scale-105 active:scale-95 self-start mt-1"
                                        style={{ backgroundColor: accentColor, color: '#ffffff' }}
                                    >
                                        {active.cta_text}
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                            {hasMedia && (
                                <MediaRenderer
                                    item={active}
                                    fitClass={fitClass}
                                    interval={carouselInterval}
                                    accent={accentColor}
                                    aspect="aspect-[4/3]"
                                    rounded="rounded-xl"
                                />
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    /* ── PILLS variant ── */
    if (variant === 'pills') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <SelectorPills />
                    <ContentPanel />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    /* ── TABS variant (default) ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="overflow-x-auto">
                    <SelectorTabs />
                </div>
                <ContentPanel />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
